import * as React from "react";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import * as z from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { PencilIcon, SearchIcon, TrashIcon } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { Entity, EntityFormData } from "@/lib/queries/entities";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  entitiesListQueryOptions,
  entityFormSchema,
} from "@/lib/queries/entities";
import { fuzzyFilter } from "@/lib/table/fuzzyFilter";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/http";
import { generateDefaultValue } from "@/lib/utils/zod";
import { useDebouncedSearch } from "@/lib/hooks";
import {
  GenericPaginationLinks,
  GenericTable,
} from "@/components/generic-table";
import { Actions } from "@/components/ui/actions";
import { useResourceAccessPermission } from "@/lib/perms";

const searchSchema = z.object({
  page: z.number().int().positive().catch(1),
  per_page: z.number().int().positive().max(100).catch(10),
  search: z.string().optional(),
});

export const Route = createFileRoute("/_app/entities/")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [stripSearchParams({ search: "" })],
  },
  loaderDeps: ({ search }) => search,
  loader({ context: { queryClient }, deps }) {
    queryClient.ensureQueryData(entitiesListQueryOptions(deps));
  },
  staticData: {
    permissionGuards: [
      {
        resource: "entities",
      },
    ],
  },
});

const columns = [
  {
    accessorKey: "name",
    header: "Name",
    size: 400,
  },
  {
    accessorKey: "description",
    header: "Description",
    size: 400,
  },
  {
    id: "actions",
    header: "Actions",
    size: 160,
    meta: { align: "right" },
    cell: ({ row: { original } }) => {
      const canEdit = useResourceAccessPermission("entities", ["update"]);
      const canDelete = useResourceAccessPermission("entities", ["delete"]);

      return (
        <Actions>
          {canEdit.data && (
            <EditDialog asChild entity={original}>
              <Button
                size="lg"
                variant="ghost"
                className="w-full justify-start text-base"
              >
                <PencilIcon className="size-5" />
                Edit
              </Button>
            </EditDialog>
          )}
          {canDelete.data && <DeleteDialog entity={original} />}
        </Actions>
      );
    },
  },
] as const satisfies Array<ColumnDef<Entity>>;

function RouteComponent() {
  const searchParams = Route.useSearch();
  const [searchQuery, setSearchQuery, rawSearchQuery] = useDebouncedSearch(
    Route.id,
    "search",
  );
  const entities = useQuery(
    entitiesListQueryOptions({ ...searchParams, search: searchQuery }),
  );

  const canEdit = useResourceAccessPermission("entities", ["update"]);
  const canDelete = useResourceAccessPermission("entities", ["delete"]);

  const showActions = Boolean(canEdit.data || canDelete.data);

  const table = useReactTable({
    data: entities.data?.items ?? [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      pagination: {
        pageIndex: searchParams.page - 1,
        pageSize: searchParams.per_page,
      },
      columnVisibility: {
        actions: showActions,
      },
    },
    rowCount: entities.data?.count ?? 1,

    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Entities</h1>
      </div>

      <div className="relative mb-4 min-w-[220px] flex-1">
        <SearchIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search all columns..."
          value={rawSearchQuery ?? ""}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="rounded-md border">
        <GenericTable table={table} isLoading={entities.isLoading} />
      </div>

      <div className="mt-4">
        <GenericPaginationLinks table={table} />
      </div>
    </div>
  );
}

function DeleteDialog({ entity }: { entity: Entity }) {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (id: number) => {
      await api.raw.delete(`api/v1/entities/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["entities", "list"] });
      queryClient.removeQueries({
        queryKey: ["entities", "detail", entity.id],
      });
      setOpen(false);
      toast.success(`Entity "${entity.name}" deleted successfully`);
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size="lg"
          variant="ghost"
          className="w-full justify-start text-base"
        >
          <TrashIcon className="size-5" />
          Delete
        </Button>
      </AlertDialogTrigger>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Entity</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the Entity "{entity.name}"? This
            action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending} asChild>
            <Button variant="outline">Cancel</Button>
          </AlertDialogCancel>

          <Button
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate(entity.id)}
          >
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditDialog({
  children,
  entity,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean; entity: Entity }>) {
  const [open, setOpen] = React.useState(false);

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, ...data }: Entity) => {
      return api.raw
        .put(`api/v1/entities/${id}`, { json: data })
        .json<Entity>();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["entities", "list"] });
      queryClient.setQueryData(["entities", "detail", variables.id], data);

      setOpen(false);
      toast.success(`Entity "${entity.name}" updated successfully`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Entity</DialogTitle>
          <EntityForm
            defaultValue={entity}
            disabled={mutation.isPending}
            buttonText={mutation.isPending ? "Saving..." : "Update"}
            onSubmit={(data) => mutation.mutate({ id: entity.id, ...data })}
            onCancel={() => setOpen(false)}
          />
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

// function CreateDialog({
//   children,
//   asChild,
// }: React.PropsWithChildren<{ asChild?: boolean }>) {
//   const [open, setOpen] = React.useState(false);

//   const queryClient = useQueryClient();

//   const mutation = useMutation({
//     mutationFn: async (data: EntityFormData) => {
//       const response = await api.raw
//         .post("api/v1/entities/", { json: data })
//         .json<Entity>();
//       return response;
//     },
//     onSuccess(data) {
//       queryClient.invalidateQueries({ queryKey: ["entities", "list"] });
//       queryClient.setQueryData(["entities", "detail", data.id], data);
//       setOpen(false);
//       toast.success(`Entity "${data.name}" created successfully`);
//     },
//   });

//   return (
//     <Dialog open={open} onOpenChange={setOpen}>
//       <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
//       <DialogContent>
//         <DialogHeader>
//           <DialogTitle>Create New Entity</DialogTitle>
//           <EntityForm
//             disabled={mutation.isPending}
//             buttonText={mutation.isPending ? "Saving..." : "Create"}
//             onSubmit={mutation.mutate}
//             onCancel={() => setOpen(false)}
//           />
//         </DialogHeader>
//       </DialogContent>
//     </Dialog>
//   );
// }

function EntityForm({
  disabled,
  buttonText,
  defaultValue,
  onSubmit,
  onCancel,
}: {
  disabled?: boolean;
  defaultValue?: EntityFormData;
  buttonText: React.ReactNode;
  onSubmit: (data: EntityFormData) => void;
  onCancel?: () => void;
}) {
  const form = useForm({
    resolver: zodResolver(entityFormSchema),
    defaultValues: {
      ...generateDefaultValue(entityFormSchema),
      ...defaultValue,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* name */}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter entity name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* description */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea placeholder="Enter entity description" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}

          <Button type="submit" disabled={disabled}>
            {buttonText}
          </Button>
        </div>
      </form>
    </Form>
  );
}
